<template>
    <div class="entrada-filters">
        <QuickViews v-if="!lockInventory" :views="quickViews" :value="quickView" @input="setQuickView" />
        <div class="filter-bar form-compact">
            <v-text-field :value="search" @input="$emit('update:search', $event)" prepend-inner-icon="fas fa-search"
                label="Buscar por folio de entrada" filled outlined hide-details clearable
                class="filter-bar__grow" @keydown.enter="$emit('search')" @click:clear="$emit('search')">
            </v-text-field>
            <v-autocomplete :value="providerId" @change="$emit('update:providerId', $event || null)"
                :items="providers" item-text="nombre" item-value="idProveedor" label="Proveedor"
                prepend-inner-icon="fas fa-truck" filled outlined hide-details clearable>
            </v-autocomplete>
            <DateRangeField :start="start" :end="end" title="Fecha" :max-today="false"
                @update:start="$emit('update:start', $event)" @update:end="$emit('update:end', $event)" />
            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-badge :content="advancedCount" :value="advancedCount > 0" color="primary" overlap bordered>
                        <v-btn icon v-bind="attrs" v-on="on" aria-label="Más filtros" @click="$emit('open-advanced')">
                            <v-icon dense>fas fa-sliders-h</v-icon>
                        </v-btn>
                    </v-badge>
                </template>
                <span>Más filtros</span>
            </v-tooltip>
        </div>
        <FilterChips :chips="chips" :total="total" @remove="removeChip" @clear="clearAll">
            <template v-if="$slots.tools" v-slot:tools><slot name="tools"></slot></template>
        </FilterChips>
    </div>
</template>
<script>
import DateRangeField from '@/components/shared/DateRangeField.vue';
import QuickViews from '@/components/shared/QuickViews.vue';
import FilterChips from '@/components/shared/FilterChips.vue';
import { describeRange } from '@/utils/dateRange';
import { emptyAdvanced, advancedChips, removeAdvanced } from './entradaFilters';

/**
 * Barra de filtros de la lista de entradas: vistas rápidas, búsqueda por
 * folio, proveedor, rango de fechas (un solo campo con atajos), botón de
 * filtros avanzados y chips con lo que está activo. Las herramientas de la
 * tabla entran por el slot `tools`.
 *
 * No consulta nada: solo edita los valores que le pasa `ListaEntrada` con
 * `.sync`. La vista los manda al backend vía `tableFilters` y el mixin
 * resetea la página.
 *
 * Los filtros del panel lateral (`advanced`) no se editan aquí: solo se
 * muestran como chips y se quitan de uno en uno. El panel es
 * `EntradaFilterDrawer`.
 *
 * `lockInventory`: en la pantalla de Inventario el filtro de existencia es
 * fijo, así que no se muestran las vistas rápidas.
 */
// Chips de la barra; el resto de las claves son del panel lateral.
const BASE_CHIPS = ['search', 'range', 'provider'];

export default {
    name: 'EntradaFilters',
    components: { DateRangeField, QuickViews, FilterChips },
    props: {
        search: { type: String, default: '' },
        start: { type: String, default: '' },
        end: { type: String, default: '' },
        providerId: { type: Number, default: null },
        providers: { type: Array, default: () => [] },
        pending: { type: Boolean, default: false },
        inventory: { type: Boolean, default: false },
        lockInventory: { type: Boolean, default: false },
        total: { type: Number, default: null },
        advanced: { type: Object, default: emptyAdvanced },
        // Cuántos filtros del panel lateral hay en uso (insignia del botón).
        advancedCount: { type: Number, default: 0 },
        // { products, sucursales, tipoEntrada }: para poner nombres en los chips.
        catalogs: { type: Object, default: () => ({}) }
    },
    data() {
        return {
            quickViews: [
                { value: 'all', label: 'Todas', icon: 'fas fa-list' },
                { value: 'inventory', label: 'Con existencia', icon: 'fas fa-warehouse' },
                { value: 'pending', label: 'Pendientes de pago', icon: 'fas fa-file-invoice-dollar' }
            ]
        };
    },
    computed: {
        quickView() {
            if (this.pending) return 'pending';
            if (this.inventory) return 'inventory';
            return 'all';
        },
        chips() {
            const chips = [];
            if (this.search) chips.push({ key: 'search', label: `Folio: ${this.search}`, icon: 'fas fa-search' });
            if (this.start || this.end) {
                chips.push({ key: 'range', label: `Fecha: ${describeRange(this.start, this.end)}`, icon: 'fas fa-calendar-alt' });
            }
            if (this.providerId) {
                const provider = this.providers.find(p => p.idProveedor === this.providerId);
                chips.push({ key: 'provider', label: `Proveedor: ${provider ? provider.nombre : this.providerId}`, icon: 'fas fa-truck' });
            }
            return chips.concat(advancedChips(this.advanced, this.catalogs));
        }
    },
    methods: {
        // Las vistas son excluyentes entre sí.
        setQuickView(view) {
            this.$emit('update:inventory', view === 'inventory');
            this.$emit('update:pending', view === 'pending');
        },
        removeChip(key) {
            if (key === 'search') {
                this.$emit('update:search', '');
                this.$emit('search');
            }
            if (key === 'range') {
                this.$emit('update:start', '');
                this.$emit('update:end', '');
            }
            if (key === 'provider') this.$emit('update:providerId', null);
            if (!BASE_CHIPS.includes(key)) this.$emit('update:advanced', removeAdvanced(this.advanced, key));
        },
        clearAll() {
            BASE_CHIPS.forEach(this.removeChip);
            this.$emit('update:advanced', emptyAdvanced());
            if (!this.lockInventory) this.setQuickView('all');
        }
    }
}
</script>
